import React, { useState } from "react";
import { BarChart3, CalendarDays, Check, Eye, Mail } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import queryClient from "@/lib/queryClient";
import { PRIVACY_CONSENT_VERSION, setAnalyticsConsent } from "@/lib/privacyConsent";
import { cacheCompliance } from "@/lib/accountCompliance";
import { AGE_GROUP_OPTIONS, getAgeGroupLabel } from "@/lib/ageGroups";
import {
  useAccount,
  useComplianceStatus,
  useDeleteAccount,
  usePrivacyPreferences,
  useUpdateComplianceStatus,
  useUpdatePrivacyPreferences,
} from "@/queries/account";

export default function DataPrivacyPanel() {
  const { data: account } = useAccount();
  const del = useDeleteAccount();
  const { data: privacyPreferences, isLoading: privacyLoading } = usePrivacyPreferences();
  const { data: complianceStatus, isLoading: complianceLoading } = useComplianceStatus();
  const updatePrivacy = useUpdatePrivacyPreferences();
  const updateCompliance = useUpdateComplianceStatus();
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [ageBracket, setAgeBracket] = useState("");
  const [editingAge, setEditingAge] = useState(false);
  const navigate = useNavigate();

  const deleteRequiresPassword = Boolean(account?.delete_requires_password);
  const optionalConsentAllowed = complianceStatus?.is_adult === true;
  const ageBracketLabel = getAgeGroupLabel(complianceStatus?.age_bracket);

  const onAgeUpdate = async () => {
    if (!ageBracket) {
      toast.error("Välj din åldersgrupp");
      return;
    }
    if (ageBracket === "under_13") {
      toast.error("Du måste ha fyllt 13 år för att använda Lapstr");
      return;
    }
    try {
      const next = await updateCompliance.mutateAsync({
        age_bracket: ageBracket,
        terms_accepted: true,
        terms_version: complianceStatus?.current_terms_version,
        source: "account_settings",
      });
      cacheCompliance(account?.id, next);
      if (!next?.is_adult) {
        setAnalyticsConsent(false, "age_restriction", account?.id);
      }
      setAgeBracket("");
      setEditingAge(false);
      toast.success("Åldersgruppen har uppdaterats");
    } catch (error) {
      toast.error(error?.message || "Kunde inte uppdatera åldersgruppen");
    }
  };

  const onAnalyticsChange = async (enabled) => {
    const previous = privacyPreferences?.analytics_enabled === true;
    setAnalyticsConsent(enabled, "server_sync", account?.id);
    try {
      await updatePrivacy.mutateAsync({
        analytics_enabled: enabled,
        consent_version: PRIVACY_CONSENT_VERSION,
        source: "account_settings",
      });
      toast.success(enabled ? "Användningsstatistik aktiverad" : "Användningsstatistik avstängd");
    } catch (error) {
      setAnalyticsConsent(previous, "server_sync", account?.id);
      toast.error(error?.message || "Kunde inte spara inställningen");
    }
  };

  const onMarketingEmailChange = async (enabled) => {
    try {
      await updatePrivacy.mutateAsync({
        marketing_email_enabled: enabled,
        consent_version: PRIVACY_CONSENT_VERSION,
        source: "account_settings",
      });
      toast.success(enabled ? "E-postutskick aktiverade" : "E-postutskick avstängda");
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara inställningen");
    }
  };

  const onDelete = async () => {
    if (confirm !== "RADERA") {
      toast('Skriv "RADERA" för att bekräfta');
      return;
    }
    if (deleteRequiresPassword && !password.trim()) {
      toast("Ange ditt lösenord för att radera kontot");
      return;
    }

    try {
      try {
        await queryClient.cancelQueries();
      } catch {}

      const deleteRequest = del.mutateAsync({
        password: deleteRequiresPassword ? password : undefined,
      });

      toast.promise(deleteRequest, {
        loading: "Tar bort konto...",
        success: "Konto borttaget",
        error: (e) => e?.message || "Kunde inte ta bort kontot",
      });

      const result = await deleteRequest;
      if (!result?.ok) {
        throw new Error("Kunde inte ta bort kontot");
      }

      try {
        localStorage.removeItem("speedway_token");
        localStorage.removeItem("speedway_user");
        sessionStorage.removeItem("auth_notice");
      } catch {}

      try {
        queryClient.clear();
      } catch {}

      try {
        window.dispatchEvent(
          new CustomEvent("auth:logout", { detail: { reason: "account_deleted" } })
        );
      } catch {}

      setTimeout(() => navigate("/auth", { replace: true }), 0);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 shrink-0 opacity-70" />
            <span>Åldersuppgifter</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label>Bekräftad åldersgrupp</Label>
              <p className="text-sm text-muted-foreground">{ageBracketLabel}</p>
            </div>
            {!editingAge ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const currentBracket = complianceStatus?.age_bracket;
                  setAgeBracket(
                    AGE_GROUP_OPTIONS.some(({ value }) => value === currentBracket)
                      ? currentBracket
                      : ""
                  );
                  setEditingAge(true);
                }}
              >
                Ändra
              </Button>
            ) : null}
          </div>

          {editingAge ? (
            <div className="native-v3-privacy-age-edit grid gap-3 border-y border-border py-4">
              <div className="grid gap-2">
                <Label>Välj åldersgrupp</Label>
                <div className="grid grid-cols-2 border border-border" role="radiogroup" aria-label="Åldersgrupp">
                  {AGE_GROUP_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={ageBracket === value}
                      className={`flex min-h-11 items-center justify-center gap-1 border-b border-r border-border px-2 text-sm font-semibold text-foreground even:border-r-0 last:col-span-2 last:border-b-0 last:border-r-0 ${ageBracket === value ? "bg-primary/10 shadow-[inset_0_-2px_0_hsl(var(--primary))]" : "bg-background"}`}
                      onClick={() => setAgeBracket(value)}
                    >
                      {ageBracket === value ? <Check className="h-4 w-4" aria-hidden="true" /> : null}
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => { setAgeBracket(""); setEditingAge(false); }}>
                  Avbryt
                </Button>
                <Button type="button" onClick={onAgeUpdate} disabled={updateCompliance.isPending || !ageBracket}>
                  {updateCompliance.isPending ? "Sparar..." : "Spara"}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 shrink-0 opacity-70" />
            <span>Användningsstatistik</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="analytics-consent">Hjälp oss förbättra Lapstr</Label>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Tillåt att Lapstr samlar in användningsstatistik för att förstå hur appen används
                och förbättra funktioner och stabilitet. Inställningen är frivillig och kan ändras
                när som helst.
              </p>
              {!complianceLoading && !optionalConsentAllowed ? (
                <p className="text-xs font-medium text-primary">
                  Avstängt för minderåriga och konton utan bekräftad ålder.
                </p>
              ) : null}
            </div>
            <Switch
              id="analytics-consent"
              checked={privacyPreferences?.analytics_enabled === true}
              disabled={privacyLoading || complianceLoading || !optionalConsentAllowed || updatePrivacy.isPending}
              onCheckedChange={onAnalyticsChange}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 shrink-0 opacity-70" />
            <span>Kommunikation och samtycken</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="marketing-email-consent">E-post från Lapstr</Label>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Ta emot frivilliga nyheter, produktuppdateringar och information via e-post.
                Nödvändiga konto-, verifierings- och säkerhetsmeddelanden påverkas inte av valet.
              </p>
              {!complianceLoading && !optionalConsentAllowed ? (
                <p className="text-xs font-medium text-primary">
                  Frivilliga marknadsföringsutskick är avstängda för minderåriga.
                </p>
              ) : null}
            </div>
            <Switch
              id="marketing-email-consent"
              checked={privacyPreferences?.marketing_email_enabled === true}
              disabled={privacyLoading || complianceLoading || !optionalConsentAllowed || updatePrivacy.isPending}
              onCheckedChange={onMarketingEmailChange}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-red-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 shrink-0 opacity-70" />
            <span>Radera konto</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="text-sm text-muted-foreground">
            Raderar ditt konto och tillhörande data. Kan inte ångras.
          </div>
          {deleteRequiresPassword ? (
            <Input
              type="password"
              placeholder="Ange ditt lösenord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          ) : (
            <div className="text-sm text-muted-foreground">
              Kontot är kopplat till extern inloggning och kräver därför ingen separat
              lösenordsbekräftelse.
            </div>
          )}
          <Input
            placeholder='Skriv "RADERA" för att bekräfta'
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={confirm !== "RADERA" || (deleteRequiresPassword && !password.trim())}
            >
              Ta bort konto
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
